import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { message, Spin } from 'antd';
import moment from "moment";
import { supabase } from '../supabaseClient';
import { calculateSubscriptionState, getNewStartDateOnResume } from '../subscriptionUtils';
import LoginModal from '../components/LoginModal';
import { LogOut, Printer, Pause, Play, XCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DigitalCardPrint from '../components/DigitalCardPrint';
import PhysicalCardBack from '../components/PhysicalCardBack';
import { QRCodeSVG } from "qrcode.react";

const MembershipVerifyScreen = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchParams] = useSearchParams();
  const [passData, setPassData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setActionLoading] = useState(false);
  const [activatedPassUrl, setActivatedPassUrl] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const printFrontRef = useRef(null);
  const printBackRef = useRef(null);

  // Check for existing session token on mount
  useEffect(() => {
    const token = sessionStorage.getItem('barista_auth_token');
    if (token === 'LOGGED_IN') {
      setIsAuthenticated(true);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Fetch data and subscribe to real-time updates once authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const data = searchParams.get("data");
    if (!data) {
      setPassData(false);
      setIsLoading(false);
      return;
    }
    let channel;
    try {
      const decodedId = JSON.parse(atob(data)).id;
      const fetchSubscription = async () => {
        setIsLoading(true);
        const { data: subscription, error } = await supabase.from('subscriptions').select('*').eq('id', decodedId).single();
        setPassData(error ? false : subscription);
        setIsLoading(false);
      };
      fetchSubscription();
      channel = supabase.channel(`verify_subscription_${decodedId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'subscriptions', filter: `id=eq.${decodedId}` },
          (payload) => setPassData(payload.new)
        ).subscribe();
    } catch (e) {
      setPassData(false);
      setIsLoading(false);
    }
    return () => { if (channel) supabase.removeChannel(channel) };
  }, [searchParams, isAuthenticated]);

  // Derive the subscription's current state using the utility function
  const subState = useMemo(() => {
    if (!passData) return { status: 'loading' };
    return calculateSubscriptionState(passData);
  }, [passData]);

  const paymentInfo = useMemo(() => {
    if (!passData || passData.status !== 'pending') return null;
    return {
      durationText: passData.duration === 3 ? '3-Month Subscription' : '1-Month Subscription',
      price: passData.price || 0,
    };
  }, [passData]);

  // --- Core Logic Handlers ---

  const handleActivate = async () => {
    if (!passData || passData.status !== 'pending' || isActionLoading) return;
    if (window.confirm("Confirm payment has been received. This will activate the pass immediately.")) {
      setActionLoading(true);
      try {
        const startDate = moment(); // Day 1 is TODAY
        const expiryDate = startDate.clone().add(passData.duration, 'months');
        const { data: updatedPass, error } = await supabase.from('subscriptions').update({
            status: 'active',
            start: startDate.format('YYYY-MM-DD'),
            expiry: expiryDate.format('YYYY-MM-DD'),
            carryover_processed_week: 0,
          }).eq('id', passData.id).select().single();
        if (error) throw new Error(error.message);
        setPassData(updatedPass);
        const newEncodedData = btoa(JSON.stringify({ id: updatedPass.id }));
        setActivatedPassUrl(`${window.location.origin}/membership/view?data=${newEncodedData}`);
        message.success('Pass Activated Successfully!');
      } catch (error) { message.error("Activation failed. Please try again."); } 
      finally { setActionLoading(false); }
    }
  };
  
  const handlePause = async () => {
    if (!passData || subState.status !== 'active' || isActionLoading) return;
    if (window.confirm("Are you sure you want to pause this subscription? The countdown will stop until it is resumed.")) {
      setActionLoading(true);
      try {
        const daysUsed = subState.redeemableDaysPassed;
        const { error } = await supabase.from('subscriptions').update({ status: 'paused', redeemable_days_used: daysUsed }).eq('id', passData.id);
        if (error) throw new Error(error.message);
        message.success('Subscription Paused.');
      } catch (error) { message.error("Failed to pause subscription."); }
      finally { setActionLoading(false); }
    }
  };
  
  const handleResume = async () => {
    if (!passData || subState.status !== 'paused' || isActionLoading) return;
    if (window.confirm("Are you sure you want to resume this subscription? The countdown will continue from where it left off.")) {
      setActionLoading(true);
      try {
        const newStartDate = getNewStartDateOnResume(passData.redeemable_days_used);
        const { error } = await supabase.from('subscriptions').update({ status: 'active', start: newStartDate }).eq('id', passData.id);
        if (error) throw new Error(error.message);
        message.success('Subscription Resumed.');
      } catch (error) { message.error("Failed to resume subscription."); }
      finally { setActionLoading(false); }
    }
  };

  const handleCancel = async () => {
    if (!passData || ['active', 'paused'].includes(subState.status) === false || isActionLoading) return;
    if (window.confirm("ARE YOU SURE you want to cancel this subscription? This action is permanent and cannot be undone.")) {
      setActionLoading(true);
      try {
        const { error } = await supabase.from('subscriptions').update({ status: 'canceled' }).eq('id', passData.id);
        if (error) throw new Error(error.message);
        message.warning('Subscription has been permanently canceled.');
      } catch (error) { message.error("Failed to cancel subscription."); }
      finally { setActionLoading(false); }
    }
  };

  const handleRedeem = async (benefitType, category = 'weeks') => {
    if (isActionLoading) return;
    const isGift = category === 'gifts';
    const currentWeekIndex = subState.currentWeek - 1;
    let benefit, confirmationMessage;
    if (isGift) {
      benefit = passData.benefits.gifts[benefitType];
      confirmationMessage = `Redeem ${benefitType.replace(/_/g, ' ')}? This is a one-time action.`;
    } else {
      benefit = passData.benefits.weeks[currentWeekIndex]?.[benefitType];
      confirmationMessage = `Redeem 1 ${benefitType} for Week ${subState.currentWeek}?`;
    }
    if (!benefit || benefit.redeemed >= benefit.total) return;
    if (window.confirm(confirmationMessage)) {
      setActionLoading(true);
      const updatedBenefits = { ...passData.benefits };
      if (isGift) updatedBenefits.gifts[benefitType].redeemed += 1;
      else updatedBenefits.weeks[currentWeekIndex][benefitType].redeemed += 1;
      const { error } = await supabase.from('subscriptions').update({ benefits: updatedBenefits }).eq('id', passData.id);
      if (error) message.error("Redemption failed.");
      else message.success(`${benefitType.replace(/_/g, ' ')} redeemed!`);
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      sessionStorage.removeItem('barista_auth_token');
      setIsAuthenticated(false);
      setPassData(null);
    }
  };
  
  const handlePrintCard = async () => {
    if (!printFrontRef.current || !printBackRef.current || isPrinting) return;
    setIsPrinting(true);
    message.loading({ content: 'Generating PDF...', key: 'pdf' });
    try {
      const cardWidth = 1063;
      const cardHeight = 591;
      const pdf = new jsPDF('landscape', 'px', [cardWidth, cardHeight]);
      const canvasFront = await html2canvas(printFrontRef.current, { backgroundColor: null, scale: 2 });
      pdf.addImage(canvasFront.toDataURL('image/png'), 'PNG', 0, 0, cardWidth, cardHeight);
      pdf.addPage();
      const canvasBack = await html2canvas(printBackRef.current, { backgroundColor: null, scale: 2 });
      pdf.addImage(canvasBack.toDataURL('image/png'), 'PNG', 0, 0, cardWidth, cardHeight);
      pdf.save(`Grams-Card-${passData.name.replace(/\s/g, '-')}.pdf`);
      message.success({ content: 'PDF Generated!', key: 'pdf', duration: 2 });
    } catch (error) { message.error({ content: 'Could not generate the PDF.', key: 'pdf', duration: 2 }); }
    setIsPrinting(false);
  };

  // --- Render Logic ---

  if (!isAuthenticated) return <LoginModal onLoginSuccess={() => setIsAuthenticated(true)} />;
  if (isLoading || passData === null) {
    return <div className="barista-verification-page"><Spin size="large" /><h1>Verifying Pass...</h1></div>;
  }
  if (passData === false) return <div className="barista-verification-page"><h1>Invalid or Missing Pass Data</h1></div>;

  const redemptionDisabled = subState.isSunday || subState.status !== 'active' || isActionLoading;
  const currentWeekBenefits = passData.benefits?.weeks?.[subState.currentWeek - 1];
  const isManageable = ['active', 'paused'].includes(subState.status);

  return (
    <div className="barista-verification-page">
      <div className="barista-dashboard">
        <header className="dashboard-header">
          <h1>Verification & Redemption</h1>
          <button onClick={handleLogout} className="logout-button" title="Log Out"><LogOut size={20} /></button>
        </header>

        {activatedPassUrl && (
          <div className="activation-success-section">
            <h2>Activation Successful!</h2>
            <p>Ask the customer to scan this QR code to access their activated pass.</p>
            <QRCodeSVG value={activatedPassUrl} size={128} />
            <a href={activatedPassUrl} target="_blank" rel="noopener noreferrer">Open Activated Pass</a>
          </div>
        )}

        <section className="member-info-section">
          <div className="info-item"><label>Member</label><p>{passData.name}</p></div>
          <div className="info-item"><label>Plan</label><p>{passData.plan}</p></div>
          <div className="info-item">
            <label>Payment</label>
            <p className="payment-amount">
              {(passData.price || 0).toFixed(2)} Dh
              <span className="duration-tag">{passData.duration === 3 ? '3-Month' : '1-Month'}</span>
            </p>
          </div>
          <div className="info-item"><label>Status</label>
            <p className={`status-tag ${subState.status}`}>{subState.status.toUpperCase()}</p>
          </div>
        </section>

        {/* --- DYNAMIC CONTENT BASED ON STATUS --- */}
        {subState.status === 'pending' && (
          <>
            <section className="payment-details-section">
              <h3>Payment Required</h3>
              <div className="payment-info-grid">
                <div className="payment-info-item"><label>Subscription Type</label><p>{paymentInfo.durationText}</p></div>
                <div className="payment-info-item total"><label>Total to Pay</label><p>{paymentInfo.price.toFixed(2)} Dh</p></div>
              </div>
            </section>
            <div className="action-section pending">
              <p>Activate only after confirming payment from the customer.</p>
              <button className="activate-button" onClick={handleActivate} disabled={isActionLoading}>
                {isActionLoading ? <Spin /> : 'Activate Pass'}
              </button>
            </div>
          </>
        )}
        
        {subState.status === 'expired' && (<div className="action-section expired"><h3>This subscription has ended.</h3><p>The customer must renew their plan to reactivate benefits.</p></div>)}
        {subState.status === 'canceled' && (<div className="action-section canceled"><h3>Subscription Canceled</h3><p>This subscription has been permanently canceled and cannot be used.</p></div>)}

        {subState.status === 'paused' && (
          <div className="action-section paused">
            <h3>Subscription Paused</h3>
            <p>The subscription is currently frozen. No benefits can be redeemed.</p>
            <p className="subtext">Paused at Week {subState.currentWeek}, Day {subState.currentDayInWeek}.</p>
          </div>
        )}

        {subState.status === 'active' && (
          <>
            {subState.isSunday && (<div className="action-section sunday"><h3>Today is Sunday — Redemption is disabled.</h3><p>Discounts and other passive benefits can still be applied.</p></div>)}
            
            <section className="benefits-section">
              <h2>Redeemable Benefits (Week {subState.currentWeek})</h2>
                <div className="benefits-grid">
                  {currentWeekBenefits?.drinks && (<BenefitCard label="Drinks" benefit={currentWeekBenefits.drinks} onRedeem={() => handleRedeem('drinks')} disabled={redemptionDisabled}/>)}
                  {currentWeekBenefits?.breakfasts && (<BenefitCard label="Breakfasts" benefit={currentWeekBenefits.breakfasts} onRedeem={() => handleRedeem('breakfasts')} disabled={redemptionDisabled}/>)}
                  {!currentWeekBenefits && subState.currentDayInWeek > 0 && <p>This plan has no weekly redeemable items.</p>}
                </div>
            </section>
            
            <OtherBenefitsBarista benefits={passData.benefits} onRedeemGift={(key) => handleRedeem(key, 'gifts')} disabled={isActionLoading}/>

            <section className="print-section">
              <h2>Physical Card</h2>
              <p>Download a printable PDF version of the membership card for the customer.</p>
              <button onClick={handlePrintCard} disabled={isPrinting} className="print-button">
                {isPrinting ? <Spin /> : <Printer size={20} />}
                {isPrinting ? 'Generating PDF...' : 'Download Card (PDF)'}
              </button>
            </section>
          </>
        )}

        {isManageable && (
           <SubscriptionManager status={subState.status} onPause={handlePause} onResume={handleResume} onCancel={handleCancel} isLoading={isActionLoading}/>
        )}

        <footer className="dashboard-footer">
          <p>Barista session active. Remember to log out at the end of your shift.</p>
        </footer>
        
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <DigitalCardPrint ref={printFrontRef} passData={passData} />
          <PhysicalCardBack ref={printBackRef} baristaLink={window.location.href} />
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SubscriptionManager = ({ status, onPause, onResume, onCancel, isLoading }) => {
  return (
    <section className="management-section">
      <h2>Manage Subscription</h2>
      <div className="management-buttons">
        {status === 'active' && (<button onClick={onPause} disabled={isLoading} className="pause-btn"><Pause size={20} /> Pause</button>)}
        {status === 'paused' && (<button onClick={onResume} disabled={isLoading} className="resume-btn"><Play size={20} /> Resume</button>)}
        <button onClick={onCancel} disabled={isLoading} className="cancel-btn"><XCircle size={20} /> Cancel</button>
      </div>
    </section>
  );
};

const BenefitCard = ({ label, benefit, onRedeem, disabled }) => {
  const remaining = benefit.total - benefit.redeemed;
  return (
    <div className="benefit-card">
      <div className="benefit-info">
        <h3>{label}</h3>
        <p className="benefit-counter">{remaining} / {benefit.total} remaining</p>
      </div>
      <div className="benefit-action">
        <button onClick={onRedeem} disabled={disabled || remaining <= 0}>Redeem 1</button>
      </div>
    </div>
  );
};

const OtherBenefitsBarista = ({ benefits, onRedeemGift, disabled }) => {
  const gifts = benefits?.gifts ? Object.entries(benefits.gifts) : [];
  const discounts = benefits?.discounts ? Object.entries(benefits.discounts) : [];
  if (gifts.length === 0 && discounts.length === 0) return null;
  return (
    <section className="benefits-section">
      <h2>Other Benefits</h2>
      <div className="benefits-grid">
        {discounts.map(([key, value]) => (
          <div key={key} className="benefit-card">
            <div className="benefit-info"><h3 className="capitalize">{key.replace(/_/g, ' ')}</h3><p className="benefit-percentage-value">{value.total} Discount</p></div>
            <div className="benefit-action"><button className="active-status-btn" disabled>Active</button></div>
          </div>
        ))}
        {gifts.map(([key, value]) => {
          const isRedeemed = value.redeemed >= 1;
          return (
            <div key={key} className="benefit-card">
              <div className="benefit-info"><h3 className="capitalize">{key.replace(/_/g, ' ')}</h3><p className="benefit-counter">{isRedeemed ? 'Already Claimed' : 'Ready to Claim'}</p></div>
              <div className="benefit-action"><button onClick={() => onRedeemGift(key)} disabled={disabled || isRedeemed}>{isRedeemed ? '✓ Claimed' : 'Mark as Claimed'}</button></div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MembershipVerifyScreen;